import { useAuth } from "@/hooks/useAuth";
import { useMedias } from "@/hooks/useMedias";
import { useScreens } from "@/hooks/useScreens";
import { useDevices } from "@/hooks/useDevices";
import { useUserPlan } from "@/hooks/useUserPlan";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { DashboardSkeleton } from "@/components/DashboardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardState } from "@/hooks/useDashboardState";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import DashboardSidebar from "@/components/dashboard/DashboardSidebar";
import DashboardSections from "@/components/dashboard/DashboardSections";
import DashboardModals from "@/components/dashboard/DashboardModals";
import { ChangePlanModal } from "@/components/dashboard/ChangePlanModal";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Menu, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import PromoModal from "@/components/dashboard/PromoModal";
import WatchAdsModal from "@/components/dashboard/WatchAdsModal";
import { StripePaymentStatus } from "@/components/StripePaymentStatus";
import { startImpersonationLog, endImpersonationLog } from "@/utils/impersonation";
import { supabase } from "@/integrations/supabase/client";

// Estilos de scrollbar para sidebar
const sidebarScrollStyles = `
  .sidebar-scroll::-webkit-scrollbar {
    width: 6px;
  }
  .sidebar-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .sidebar-scroll::-webkit-scrollbar-thumb {
    background: #374151;
    border-radius: 3px;
  }
  .sidebar-scroll::-webkit-scrollbar-thumb:hover {
    background: #1f2937;
  }
`;

const Dashboard = () => {
  // TODOS OS HOOKS DEVEM SER CHAMADOS PRIMEIRO, ANTES DE QUALQUER RETURN CONDICIONAL
  const { user, signOut, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const impersonatedUserId = searchParams.get('impersonate') || undefined;
  const impersonatedOrgId = searchParams.get('org') || undefined;
  const isImpersonating = !!impersonatedUserId && !!user?.email && user.email.toLowerCase().includes('admin');
  const effectiveUserId = isImpersonating ? impersonatedUserId : (user?.id || undefined);

  // DEBUG: Log detalhado do modo impersonate
  console.log('🎭 DASHBOARD IMPERSONATE DEBUG:', {
    impersonatedUserId,
    impersonatedOrgId,
    isImpersonating,
    effectiveUserId,
    adminUserId: user?.id,
    adminEmail: user?.email,
    urlParams: window.location.search
  });

  // Hook para verificar plano do usuário
  const { hasActivePlan, isInTrialPeriod, loading: planLoading } = useUserPlan(effectiveUserId || '');

  // Hooks para buscar dados (só executar quando user estiver disponível)
  const mediasHook = useMedias(effectiveUserId);
  const { medias, loading: mediasLoading, createMedia, updateMedia, deleteMedia, refetch: refetchMedias } = mediasHook;
  const { screens, loading: screensLoading, createScreen, updateScreen, deleteScreen } = useScreens(effectiveUserId, impersonatedOrgId);
  const { devices, loading: devicesLoading, activateDevice, updateDevice, removeDevice, updateDeviceScreen } = useDevices(effectiveUserId, impersonatedOrgId);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Estado para controlar visibilidade do sidebar
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  // Refatorado: usar hook para estados do dashboard
  const {
    showCreateMediaModal, setShowCreateMediaModal,
    showCreateScreenModal, setShowCreateScreenModal,
    showEditMediaModal, setShowEditMediaModal,
    showEditScreenModal, setShowEditScreenModal,
    showDeviceActivationModal, setShowDeviceActivationModal,
    selectedMedia, setSelectedMedia,
    selectedScreen, setSelectedScreen,
    isActivatingDevice, setIsActivatingDevice,
    isDrawerOpen, setIsDrawerOpen,
    showCreativesModal, setShowCreativesModal,
    showChangePasswordModal, setShowChangePasswordModal,
    showProfileModal, setShowProfileModal,
    showPrivacyPolicyModal, setShowPrivacyPolicyModal,
    showRequiredInfoModal, setShowRequiredInfoModal,
    showMyPlanModal, setShowMyPlanModal,
    showPaymentHistoryModal, setShowPaymentHistoryModal,
    showVenuePartnerModal, setShowVenuePartnerModal,
    showNoActiveDevicesModal, setShowNoActiveDevicesModal,
    showPlayerScheduleModal, setShowPlayerScheduleModal,
  } = useDashboardState();

  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showWatchAdsModal, setShowWatchAdsModal] = useState(false);

  const [shouldOpenPlanModal, setShouldOpenPlanModal] = useState(false);
  const [selectedPlanFromLanding, setSelectedPlanFromLanding] = useState<string | null>(null);
  const [screensCountOverride, setScreensCountOverride] = useState<number | undefined>(undefined);
  const [shouldOpenTrialUpgradeModal, setShouldOpenTrialUpgradeModal] = useState(false);

  useEffect(() => {
    if (!isImpersonating) {
      setScreensCountOverride(undefined);
      return;
    }
    setScreensCountOverride((screens || []).length);
  }, [isImpersonating, screens]);

  // Verificar se há parâmetros de pagamento do Stripe
  const hasPaymentParams = searchParams.get('checkout') || searchParams.get('success') || searchParams.get('canceled') || searchParams.get('session_id');

  // Redirecionar para página de login se não estiver autenticado, mas só após o carregamento da autenticação
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('📱 Dashboard - Usuário não autenticado, redirecionando...');
      navigate('/auth');
    } else if (user) {
      console.log('📱 Dashboard - Usuário autenticado:', user.id);
      if (isImpersonating) {
        startImpersonationLog(user.id, user.email || undefined, impersonatedUserId || undefined, undefined, impersonatedOrgId || undefined)
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (isImpersonating) {
      setShowPrivacyPolicyModal(false);
      setShowRequiredInfoModal(false);
      return;
    }
    if (profile && user) {
      const accepted = (profile as any).privacy_policy_accepted === true
      setShowPrivacyPolicyModal(!accepted)
      if (accepted) {
        const needsInfo = !profile?.phone || !profile?.cpf_cnpj || !profile?.street || !profile?.number || !profile?.neighborhood || !profile?.city || !profile?.state || !profile?.cep
        setShowRequiredInfoModal(!!needsInfo)
      } else {
        setShowRequiredInfoModal(false)
      }
    }
  }, [profile, user, isImpersonating]);

  useEffect(() => {
    const handler = () => {
      setShowPrivacyPolicyModal(false)
      setShowRequiredInfoModal(true)
    }
    window.addEventListener('openRequiredInfoModal', handler)
    return () => window.removeEventListener('openRequiredInfoModal', handler)
  }, [])

  // Listener para refresh forçado das mídias (drag & drop)
  useEffect(() => {
    const handleForceMediasRefresh = async () => {
      console.log('🔄 Dashboard - Refresh forçado das mídias solicitado');
      try {
        // Recarregar mídias após reorganização
        await refetchMedias();
        console.log('✅ Dashboard - Mídias recarregadas com sucesso');
      } catch (error) {
        console.error('❌ Dashboard - Erro no refresh forçado:', error);
      }
    };

    window.addEventListener('forceMediasRefresh', handleForceMediasRefresh);

    return () => {
      window.removeEventListener('forceMediasRefresh', handleForceMediasRefresh);
    };
  }, [refetchMedias]);

  useEffect(() => {
    const openPlanHandler = () => {
      if (isImpersonating) return;
      if (!planLoading && !hasActivePlan) setShouldOpenPlanModal(true);
    };
    window.addEventListener('openPlanModal', openPlanHandler);
    return () => window.removeEventListener('openPlanModal', openPlanHandler);
  }, [hasActivePlan, planLoading, isImpersonating]);

  useEffect(() => {
    if (hasActivePlan) {
      setShouldOpenPlanModal(false);
      setSelectedPlanFromLanding(null);
    }
  }, [hasActivePlan]);

  useEffect(() => {
    // Se há parâmetros de pagamento (retorno do checkout), NÃO abrir modal de planos
    // Deixar apenas o modal de "Pagamento Aprovado" aparecer
    if (hasPaymentParams) {
      setShouldOpenPlanModal(false);
      return;
    }

    if (isImpersonating) {
      setShouldOpenPlanModal(false);
      setSelectedPlanFromLanding(null);
      return;
    }
    if (!user || authLoading || planLoading) return;
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      setSelectedPlanFromLanding(storedPlan);
      localStorage.removeItem('selectedPlan');
      setShouldOpenPlanModal(true);
      return;
    }
    if (!hasActivePlan) {
      setShouldOpenPlanModal(true);
      toast({
        title: 'Assinatura necessária',
        description: 'Assine um plano para liberar todas as funcionalidades.',
      });
    } else {
      setShouldOpenPlanModal(false);
    }
  }, [user, authLoading, planLoading, hasActivePlan, toast, isImpersonating, hasPaymentParams]);

  // AGORA SIM, APÓS TODOS OS HOOKS, PODEMOS TER RETURNS CONDICIONAIS

  // Se ainda está carregando a autenticação, mostrar loading
  if (authLoading) {
    console.log('📱 Dashboard - Carregando autenticação...');
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não há usuário após o carregamento, não renderizar nada (será redirecionado)
  if (!user) {
    return null;
  }

  // Refatorado: logica de loading menos agressiva para evitar unmount durante refresh
  // Só consideramos loading crítico se não tivermos dados essenciais ainda
  const isInitialLoading = (mediasLoading && !medias) || (screensLoading && !screens) || (devicesLoading && !devices) || (isImpersonating ? false : (planLoading && hasActivePlan === undefined));

  const handleLogout = async () => {
    try {
      console.log('🔄 Dashboard - Iniciando logout...');
      await signOut();

      // Aguardar um pouco para garantir que o logout seja processado
      setTimeout(() => {
        console.log('🔄 Dashboard - Redirecionando para /auth');
        navigate('/auth');
      }, 500);

    } catch (error) {
      console.error('❌ Dashboard - Erro ao fazer logout:', error);
    }
  };


  // Função para atualizar tempo de exibição das mídias
  const handleUpdateDisplayTime = async (mediaId: string, displayTime: number) => {
    try {
      console.log('🕰️ Atualizando tempo de exibição:', { mediaId, displayTime });

      const result = await updateMedia(mediaId, { display_time: displayTime });

      if (result) {
        console.log('✅ Tempo de exibição atualizado com sucesso');

        // Salvar informações da mudança no localStorage para notificação no player
        const changeData = {
          mediaId,
          newTime: displayTime,
          showNotification: true,
          timestamp: Date.now()
        };
        localStorage.setItem('displayTimeChanged', JSON.stringify(changeData));

        // Disparar evento personalizado para atualizar players em tempo real
        window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
          detail: {
            type: 'media_display_time_updated',
            mediaId,
            displayTime,
            timestamp: Date.now()
          }
        }));

        // Refresh global de todos os players ativos
        window.dispatchEvent(new CustomEvent('dashboardRefreshAllPlayers', {
          detail: {
            reason: 'Display time updated',
            mediaId,
            displayTime
          }
        }));

        console.log('📡 Eventos de atualização disparados');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar tempo de exibição:', error);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-background">
        <DashboardSkeleton />
      </div>
    );
  }

  const handleCreateMedia = async (mediaData: any) => {
    console.log('📝 Dashboard - handleCreateMedia chamado com:', mediaData);
    console.log('👤 Dashboard - User atual:', user);
    console.log('🎭 Dashboard - Modo Impersonate:', { isImpersonating, effectiveUserId });

    // Usar effectiveUserId para suportar modo impersonate
    const targetUserId = effectiveUserId || user?.id;

    if (!targetUserId) {
      toast({
        title: 'Erro de autenticação',
        description: 'Usuário não encontrado. Faça login novamente.',
        variant: 'destructive',
      });
      return;
    }

    // Adicionar o user_id e organization_id aos dados da mídia
    // Usar effectiveUserId para permitir que Admin Master crie mídias para o usuário impersonado
    const mediaWithUserId = {
      ...mediaData,
      user_id: targetUserId,
      organization_id: isImpersonating ? (impersonatedOrgId || 'fe3bd8b7-d5ad-4cb4-8ee6-cbf0a3a5b2d2') : (user?.organization_id || 'fe3bd8b7-d5ad-4cb4-8ee6-cbf0a3a5b2d2'),
      type: 'link' // Tipo padrão para mídias de link
    };

    console.log('📝 Dashboard - Dados da mídia com user_id:', mediaWithUserId);

    const newMedia = await createMedia(mediaWithUserId);
    setShowCreateMediaModal(false);

    if (newMedia) {
      console.log('🎬 Dashboard - Nova mídia criada:', {
        id: newMedia.id,
        title: newMedia.title,
        selected_screens: newMedia.selected_screens,
        user_id: newMedia.user_id
      });

      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'media_created',
          data: newMedia
        }
      }));
      console.log('📢 Dashboard - Evento de nova mídia disparado para todos os dispositivos');

      // Forçar atualização global após breve delay
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('dashboardRefreshAllPlayers'));
        console.log('📢 Dashboard - Evento de refresh disparado para todos os players');
      }, 500);

      // Evento específico para players que monitoram mídias em tempo real
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('newMediaCreated', {
          detail: {
            media: newMedia,
            timestamp: new Date().toISOString()
          }
        }));
        console.log('📢 Dashboard - Evento newMediaCreated disparado');
      }, 1000);
    }
  };

  const handleUpdateMedia = async (mediaData: any) => {
    try {
      const result = await updateMedia(mediaData.id, {
        title: mediaData.title,
        description: mediaData.description,
        url: mediaData.url,
        selected_screens: mediaData.selected_screens,
        display_time: mediaData.display_time,
        position_config: mediaData.position_config,
      });

      if (result) {
        setShowEditMediaModal(false);
        console.log('✅ Dashboard - Mídia atualizada com sucesso:', result);

        // Notificar atualização global
        window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
          detail: {
            type: 'media_updated',
            data: {
              ...result,
              selected_screens: mediaData.selected_screens
            }
          }
        }));

        // Forçar atualização imediata nos players
        window.dispatchEvent(new CustomEvent('dashboardRefreshAllPlayers'));
      }
    } catch (error) {
      console.error('❌ Dashboard - Erro ao editar mídia:', error);
      toast({
        variant: "destructive",
        title: "Erro ao editar mídia",
        description: error instanceof Error ? error.message : "Erro desconhecido"
      });
    }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    const success = await deleteMedia(mediaId);

    if (success) {
      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'media_deleted',
          mediaId
        }
      }));
      console.log('📢 Dashboard - Evento de mídia deletada disparado para todos os dispositivos');
    }
  };

  const handleCreateScreen = async (screenData: { title: string; description: string }) => {
    console.log('📝 Dashboard - Criando tela:', screenData);
    const newScreen = await createScreen({
      name: screenData.title,
      settings: { description: screenData.description }
    });

    if (newScreen) {
      setShowCreateScreenModal(false);

      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'screen_created',
          data: newScreen
        }
      }));
      console.log('📢 Dashboard - Evento de nova tela disparado para todos os dispositivos');
    }
  };

  const handleUpdateScreen = async (screenData: { id: string; title: string; description: string }) => {
    console.log('📝 Dashboard - Atualizando tela:', screenData);
    const updatedScreen = await updateScreen(screenData.id, {
      name: screenData.title,
      settings: { description: screenData.description }
    });

    if (updatedScreen) {
      setShowEditScreenModal(false);

      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'screen_updated',
          data: updatedScreen
        }
      }));
      console.log('📢 Dashboard - Evento de tela atualizada disparado para todos os dispositivos');
    }
  };

  const handleDeleteScreen = async (screenId: string) => {
    console.log('🗑️ Dashboard - Deletando tela:', screenId);
    const success = await deleteScreen(screenId);

    if (success) {
      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'screen_deleted',
          screenId
        }
      }));
      console.log('📢 Dashboard - Evento de tela deletada disparado para todos os dispositivos');
    }
  };

  const handleTrialUpgrade = () => {
    setShouldOpenTrialUpgradeModal(true);
  };

  const handleActivateDevice = async (deviceCode: string, screenId: string, deviceName?: string) => {
    try {
      setIsActivatingDevice(true);
      console.log('📱 Dashboard - Ativando dispositivo:', { deviceCode, screenId });
      const newDevice = await activateDevice(deviceCode, screenId, deviceName);

      if (newDevice) {
        setShowDeviceActivationModal(false);

        // Disparar evento global para todos os dispositivos
        window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
          detail: {
            type: 'device_activated',
            data: newDevice
          }
        }));
        console.log('📢 Dashboard - Evento de dispositivo ativado disparado para todos os dispositivos');
        return true;
      }
      return false;
    } finally {
      setIsActivatingDevice(false);
    }
  };

  const handleUpdateDevice = async (deviceId: string, updates: any) => {
    console.log('📱 Dashboard - Atualizando dispositivo:', { deviceId, updates });
    const updatedDevice = await updateDevice(deviceId, updates);

    if (updatedDevice) {
      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'device_updated',
          data: updatedDevice
        }
      }));
      console.log('📢 Dashboard - Evento de dispositivo atualizado disparado para todos os dispositivos');
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    console.log('🗑️ Dashboard - Removendo dispositivo:', deviceId);
    const success = await removeDevice(deviceId);

    if (success) {
      // Forçar re-fetch dos dispositivos após um delay para garantir que a remoção foi processada
      setTimeout(async () => {
        console.log('🔄 Dashboard - Re-fetching dispositivos após remoção');
        try {
          // Usar window.location.reload() temporariamente para garantir sincronização
          window.location.reload();
        } catch (error) {
          console.error('❌ Dashboard - Erro no re-fetch:', error);
        }
      }, 500);

      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'device_removed',
          deviceId
        }
      }));
      console.log('📢 Dashboard - Evento de dispositivo removido disparado para todos os dispositivos');
    }
  };

  const handleUpdateDeviceScreen = async (deviceId: string, screenId: string) => {
    console.log('📱 Dashboard - Atualizando tela do dispositivo:', { deviceId, screenId });
    const updatedDevice = await updateDeviceScreen(deviceId, screenId);

    if (updatedDevice) {
      // Disparar evento global para todos os dispositivos
      window.dispatchEvent(new CustomEvent('dashboardDataUpdate', {
        detail: {
          type: 'device_screen_updated',
          data: updatedDevice
        }
      }));
      console.log('📢 Dashboard - Evento de tela do dispositivo atualizada disparado para todos os dispositivos');
    }
  };

  const hasActiveDevices = devices?.some(d => d.status === 'active') || false;

  // Renderização principal do dashboard
  return (
    <>
      <style>{sidebarScrollStyles}</style>
      <div className={`min-h-screen bg-gray-100 dark:bg-background ${isImpersonating ? 'pt-10' : ''}`}>
        {/* Tarja de Impersonação - Fixa no topo, não afeta layout */}
        {isImpersonating && (
          <div className="fixed top-0 left-0 right-0 z-[100] w-full bg-yellow-100 border-b border-yellow-300 text-yellow-900 px-3 py-2 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="text-sm font-medium">Você está visualizando como <strong>{profile?.full_name || 'Usuário'}</strong></span>
            </div>
            <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={async () => {
              await endImpersonationLog()
              const url = new URL(window.location.href)
              url.searchParams.delete('impersonate')
              url.searchParams.delete('org')
              window.location.href = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '')
            }}>
              Sair
            </Button>
          </div>
        )}
        {/* Header */}
        <DashboardHeader
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          onNewDevice={() => {
            if (planLoading) {
              toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
              return;
            }
            if (!hasActivePlan) {
              setShouldOpenPlanModal(true);
              toast({ title: 'Assinatura necessária', description: 'Ative um plano para cadastrar dispositivos.' });
              return;
            }
            setShowDeviceActivationModal(true);
          }}
        />
        {/* Conteúdo principal */}
        <div className="flex h-screen">
          {/* Sidebar para desktop */}
          {!isMobile && isSidebarVisible && (
            <div className={`fixed left-0 z-30 overflow-y-auto sidebar-scroll ${isImpersonating ? 'top-[120px] h-[calc(100vh-120px)]' : 'top-20 h-[calc(100vh-80px)]'}`}>
              <DashboardSidebar
                screens={screens || []}
                hasActiveDevices={hasActiveDevices}
                onNewScreen={() => {
                  if (planLoading) {
                    toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                    return;
                  }
                  if (!hasActivePlan) {
                    setShouldOpenPlanModal(true);
                    toast({ title: 'Assinatura necessária', description: 'Ative um plano para criar telas.' });
                    return;
                  }
                  setShowCreateScreenModal(true);
                }}
                onNewMedia={() => {
                  if (planLoading) {
                    toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                    return;
                  }
                  if (!hasActivePlan) {
                    setShouldOpenPlanModal(true);
                    toast({ title: 'Assinatura necessária', description: 'Ative um plano para criar mídias.' });
                    return;
                  }
                  setShowCreateMediaModal(true);
                }}
                onNewDevice={() => {
                  if (planLoading) {
                    toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                    return;
                  }
                  if (!hasActivePlan) {
                    setShouldOpenPlanModal(true);
                    toast({ title: 'Assinatura necessária', description: 'Ative um plano para cadastrar dispositivos.' });
                    return;
                  }
                  setShowDeviceActivationModal(true);
                }}
                onNewPromo={() => {
                  if (planLoading) {
                    toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                    return;
                  }
                  if (!hasActivePlan) {
                    setShouldOpenPlanModal(true);
                    toast({ title: 'Assinatura necessária', description: 'Ative um plano para criar promoções.' });
                    return;
                  }
                  setShowPromoModal(true);
                }}
                onNewCreatives={() => setShowCreativesModal(true)}
                onWatchAds={() => {
                  if (planLoading) {
                    toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                    return;
                  }
                  if (!hasActivePlan) {
                    setShouldOpenPlanModal(true);
                    toast({ title: 'Assinatura necessária', description: 'Ative um plano para usar Watch Ads.' });
                    return;
                  }
                  setShowWatchAdsModal(true);
                }}
                onOpenProfile={() => setShowProfileModal(true)}
                onChangePassword={() => setShowChangePasswordModal(true)}
                onOpenMyPlan={() => setShowMyPlanModal(true)}
                onOpenPaymentHistory={() => setShowPaymentHistoryModal(true)}
                onOpenVenuePartner={() => setShowVenuePartnerModal(true)}
                onOpenNoActiveDevices={() => setShowNoActiveDevicesModal(true)}
                onOpenPlayerSchedule={() => setShowPlayerScheduleModal(true)}
              />
            </div>
          )}

          {/* Botão toggle do sidebar - apenas para desktop */}
          {!isMobile && (
            <Button
              onClick={() => setIsSidebarVisible(!isSidebarVisible)}
              className={`fixed z-30 h-10 w-10 p-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg hover:bg-green-50 dark:hover:bg-green-50 transition-all duration-300 group ${isSidebarVisible ? 'left-72' : 'left-2'} ${isImpersonating ? 'top-[136px]' : 'top-24'}`}
              variant="outline"
              title={isSidebarVisible ? "Recolher sidebar" : "Expandir sidebar"}
            >
              {isSidebarVisible ? (
                <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-300 group-hover:text-green-700 transition-colors duration-300" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-300 group-hover:text-green-700 transition-colors duration-300" />
              )}
            </Button>
          )}

          {/* Conteúdo principal */}
          <main className={`flex-1 p-4 md:p-6 overflow-y-auto transition-all duration-300 ${!isMobile && isSidebarVisible ? 'ml-80' : ''}`}>
            <DashboardSections
              medias={medias || []}
              screens={screens || []}
              devices={devices || []}
              screensCountOverride={screensCountOverride}
              handleEditMedia={(media) => {
                if (planLoading) {
                  toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                  return;
                }
                if (!hasActivePlan) {
                  setShouldOpenPlanModal(true);
                  toast({ title: 'Assinatura necessária', description: 'Ative um plano para editar mídias.' });
                  return;
                }
                setSelectedMedia(media);
                setShowEditMediaModal(true);
              }}
              handleDeleteMedia={handleDeleteMedia}
              handleUpdateDisplayTime={handleUpdateDisplayTime}
              handleEditScreen={(screen) => {
                if (planLoading) {
                  toast({ title: 'Carregando plano...', description: 'Aguarde a verificação da sua assinatura.' });
                  return;
                }
                if (!hasActivePlan) {
                  setShouldOpenPlanModal(true);
                  toast({ title: 'Assinatura necessária', description: 'Ative um plano para editar telas.' });
                  return;
                }
                setSelectedScreen(screen);
                setShowEditScreenModal(true);
              }}
              handleDeleteScreen={handleDeleteScreen}
              handleDeleteDevice={handleRemoveDevice}
              handleUpdateDeviceScreen={handleUpdateDeviceScreen}
              handleUpdateDeviceName={handleUpdateDevice}
              handleUpdateMedia={handleUpdateMedia}
            />
          </main>
          {/* Drawer para mobile */}
          {isMobile && (
            <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
              <DrawerContent>
                <div className="p-4">
                  <DashboardSidebar
                    screens={screens}
                    hasActiveDevices={hasActiveDevices}
                    onNewScreen={() => {
                      setIsDrawerOpen(false);
                      setShowCreateScreenModal(true);
                    }}
                    onNewMedia={() => {
                      setIsDrawerOpen(false);
                      setShowCreateMediaModal(true);
                    }}
                    onNewDevice={() => {
                      setIsDrawerOpen(false);
                      setShowDeviceActivationModal(true);
                    }}
                    onNewPromo={() => {
                      setIsDrawerOpen(false);
                      setShowPromoModal(true);
                    }}
                    onNewCreatives={() => {
                      setIsDrawerOpen(false);
                      setShowCreativesModal(true);
                    }}
                    onWatchAds={() => {
                      setIsDrawerOpen(false);
                      setShowWatchAdsModal(true);
                    }}
                    onOpenProfile={() => {
                      setIsDrawerOpen(false);
                      setShowProfileModal(true);
                    }}
                    onChangePassword={() => {
                      setIsDrawerOpen(false);
                      setShowChangePasswordModal(true);
                    }}
                    onOpenMyPlan={() => {
                      setIsDrawerOpen(false);
                      setShowMyPlanModal(true);
                    }}
                    onOpenPaymentHistory={() => {
                      setIsDrawerOpen(false);
                      setShowPaymentHistoryModal(true);
                    }}
                    onOpenVenuePartner={() => {
                      setIsDrawerOpen(false);
                      setShowVenuePartnerModal(true);
                    }}
                    onOpenNoActiveDevices={() => {
                      setIsDrawerOpen(false);
                      setShowNoActiveDevicesModal(true);
                    }}
                    onOpenPlayerSchedule={() => {
                      setIsDrawerOpen(false);
                      setShowPlayerScheduleModal(true);
                    }}
                    onClose={() => setIsDrawerOpen(false)}
                  />
                </div>
              </DrawerContent>
            </Drawer>
          )}
        </div>
        {/* Modais */}
        <DashboardModals
          showCreateMediaModal={showCreateMediaModal}
          setShowCreateMediaModal={setShowCreateMediaModal}
          handleCreateMedia={handleCreateMedia}
          screens={screens}
          medias={medias}
          showCreateScreenModal={showCreateScreenModal}
          setShowCreateScreenModal={setShowCreateScreenModal}
          handleCreateScreen={handleCreateScreen}
          showEditMediaModal={showEditMediaModal}
          setShowEditMediaModal={setShowEditMediaModal}
          handleUpdateMedia={handleUpdateMedia}
          selectedMedia={selectedMedia}
          showEditScreenModal={showEditScreenModal}
          setShowEditScreenModal={setShowEditScreenModal}
          handleUpdateScreen={handleUpdateScreen}
          selectedScreen={selectedScreen}
          showDeviceActivationModal={showDeviceActivationModal}
          setShowDeviceActivationModal={setShowDeviceActivationModal}
          handleCreateDevice={handleActivateDevice}
          isActivatingDevice={isActivatingDevice}
          onUpgradeClick={handleTrialUpgrade}
          showCreativesModal={showCreativesModal}
          setShowCreativesModal={setShowCreativesModal}
          showChangePasswordModal={showChangePasswordModal}
          setShowChangePasswordModal={setShowChangePasswordModal}
          showProfileModal={showProfileModal}
          setShowProfileModal={setShowProfileModal}
          showPrivacyPolicyModal={showPrivacyPolicyModal}
          setShowPrivacyPolicyModal={setShowPrivacyPolicyModal}
          showRequiredInfoModal={showRequiredInfoModal}
          setShowRequiredInfoModal={setShowRequiredInfoModal}
          showMyPlanModal={showMyPlanModal}
          setShowMyPlanModal={setShowMyPlanModal}
          showPaymentHistoryModal={showPaymentHistoryModal}
          setShowPaymentHistoryModal={setShowPaymentHistoryModal}
          showVenuePartnerModal={showVenuePartnerModal}
          setShowVenuePartnerModal={setShowVenuePartnerModal}
          showNoActiveDevicesModal={showNoActiveDevicesModal}
          setShowNoActiveDevicesModal={setShowNoActiveDevicesModal}
          showPlayerScheduleModal={showPlayerScheduleModal}
          setShowPlayerScheduleModal={setShowPlayerScheduleModal}
        />
        {!isImpersonating && !hasActivePlan && shouldOpenPlanModal && (
          <ChangePlanModal
            isOpen={shouldOpenPlanModal}
            onClose={() => setShouldOpenPlanModal(false)}
            currentPlan={selectedPlanFromLanding}
          />
        )}
        {/* Modal de upgrade para usuários Trial */}
        {shouldOpenTrialUpgradeModal && (
          <ChangePlanModal
            isOpen={shouldOpenTrialUpgradeModal}
            onClose={() => setShouldOpenTrialUpgradeModal(false)}
            currentPlan={null}
          />
        )}
        {/* Modal de promoção */}
        {showPromoModal && (
          <PromoModal
            open={showPromoModal}
            onClose={() => setShowPromoModal(false)}
            screens={screens}
          />
        )}
        {/* Modal Watch Ads */}
        {showWatchAdsModal && (
          <WatchAdsModal
            open={showWatchAdsModal}
            onClose={() => setShowWatchAdsModal(false)}
            medias={medias || []}
          />
        )}
        {/* Status do pagamento Stripe */}
        {hasPaymentParams && (
          <StripePaymentStatus />
        )}

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-6 mt-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-400">
              © 2026 Watch Technologies | Smart Media. Todos os direitos reservados.
            </p>
          </div>
        </footer>

      </div>
    </>
  );
};

export default Dashboard;
